/**
 * Compresses an image file using a native Canvas approach (faster than library-based methods).
 * 
 * @param {File} file - The file to compress.
 * @param {Object} options - Compression options.
 * @param {number} options.maxWidthOrHeight - Maximum width or height (default 1920).
 * @param {number} options.quality - Compression quality between 0 and 1 (default 0.7).
 * @returns {Promise<File>} - The compressed file (or original if it's not an image/fails).
 */
export const compressImage = async (file, options = {}) => {
  if (!file || !file.type.startsWith('image/')) {
    return file;
  }

  const { maxWidthOrHeight = 1920, quality = 0.7 } = options;

  console.log(`[Compression] Starting for ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
  const startTime = performance.now();

  try {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize if necessary
          if (width > height) {
            if (width > maxWidthOrHeight) {
              height = Math.round((height * maxWidthOrHeight) / width);
              width = maxWidthOrHeight;
            }
          } else {
            if (height > maxWidthOrHeight) {
              width = Math.round((width * maxWidthOrHeight) / height);
              height = maxWidthOrHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Canvas toBlob failed'));
                return;
              }

              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg', // Force JPEG for better compression
                lastModified: Date.now(),
              });

              const endTime = performance.now();
              console.log(`[Compression] Finished ${file.name} in ${(endTime - startTime).toFixed(0)}ms`);
              console.log(`[Compression] New size: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
              
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  } catch (error) {
    console.error('[Compression] Error:', error);
    return file; // Return original if fails
  }
};
